import { Box, ThemeProvider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { FlexcellMark } from "../../ui/theme/FlexcellMark";
import { accentedConsoleTheme } from "../../ui/theme/consoleTheme";
import { useTokens } from "../../ui/theme/theme";

// ---
// The frame for a page somebody reached from a link in their mail
//
// One box on a background, and nothing else: a mark or a logo, a heading, a sentence, the form.
//
// Deliberately not the sign-in layout. That one gives half the screen to a panel describing
// FlexAuth — the right thing on a page an administrator chose to visit, and the wrong thing for
// somebody who has never heard of us and is here because their employer invited them. Half a
// screen of a stranger's marketing is how a legitimate message starts to look like a phish.
//
// So this frame says only what the tenant puts in it. With no branding it falls back to FlexAuth's
// own mark and name, which is honest rather than promotional.
// ---

/** What a tenant has put on these pages. Every field falls back to what FlexAuth ships. */
export interface PageBranding {
  readonly displayName: string | null;
  readonly logoUrl: string | null;
  readonly accent: string | null;
  readonly supportEmail: string | null;
}

/**
 * Repaints a subtree in the tenant's colour, by rebuilding the theme its controls read.
 *
 * The colours are baked into `styleOverrides` when a theme is built, so a nested provider that
 * swaps `palette.tokens` repaints nothing — the overrides captured their values already.
 */
export function Accented({
  accent,
  children,
}: { accent: string; children: ReactNode }): React.JSX.Element {
  const mode = useTheme().palette.mode;
  return <ThemeProvider theme={accentedConsoleTheme(mode, accent)}>{children}</ThemeProvider>;
}

export interface LandingContent {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly brand?: PageBranding | null;
}

/**
 * The box itself, without the screen it is centred on.
 *
 * Separate so the console can show one — the preview there renders this very component, which is
 * the only way a preview is a preview rather than a drawing of one that drifts.
 */
export function LandingBox({
  title,
  subtitle,
  children,
  footer,
  brand,
}: LandingContent): React.JSX.Element {
  const tokens = useTokens();
  const accent = brand?.accent ?? tokens.accent.base;
  const name = brand?.displayName ?? "FlexAuth";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        bgcolor: tokens.surface.card,
        border: `1px solid ${tokens.line.default}`,
        borderRadius: `${tokens.radius.xl}px`,
        boxShadow: tokens.shadow[2],
        px: { xs: 6, sm: 9 },
        py: 9,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 7 }}>
        {brand?.logoUrl == null ? (
          <FlexcellMark size={22} color={accent} title={name} />
        ) : (
          <Box
            component="img"
            src={brand.logoUrl}
            alt={name}
            sx={{ height: 26, maxWidth: 170, objectFit: "contain" }}
          />
        )}
        <Box sx={{ fontSize: 15, fontWeight: tokens.weight.semibold, color: tokens.ink.primary }}>
          {name}
        </Box>
      </Box>

      <Box
        component="h1"
        sx={{ typography: "h1", fontSize: "1.5rem", color: tokens.ink.primary, m: 0, mb: 2 }}
      >
        {title}
      </Box>
      {subtitle !== undefined && (
        <Box sx={{ typography: "body1", color: tokens.ink.muted, mb: 6 }}>{subtitle}</Box>
      )}

      {children}

      {(footer !== undefined || brand?.supportEmail != null) && (
        <Box
          sx={{
            mt: 7,
            pt: 5,
            borderTop: `1px solid ${tokens.line.faint}`,
            typography: "caption",
            color: tokens.ink.muted,
            textAlign: "center",
          }}
        >
          {footer}
          {brand?.supportEmail != null && <Box>Need help? {brand.supportEmail}</Box>}
        </Box>
      )}
    </Box>
  );
}

export function LandingLayout(props: LandingContent): React.JSX.Element {
  const tokens = useTokens();
  const box = <LandingBox {...props} />;
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: tokens.surface.bg,
        display: "grid",
        placeItems: "center",
        // Room to breathe on a phone, where the box is the whole screen.
        p: { xs: 4, sm: 6 },
      }}
    >
      {props.brand?.accent == null ? box : <Accented accent={props.brand.accent}>{box}</Accented>}
    </Box>
  );
}
